// src/components/PackageLabels.js
// 包裝標籤管理：產生 QR 標籤 → 列印 → 掃描入庫防重複
import { useState, useEffect, useCallback } from 'react'
import QRCode from 'qrcode'
import { supabase } from '../supabaseClient'
import { C, st, formatDate } from '../theme'
import { SearchSelect } from './UI'

const STATUS_LABEL = {
  pending:    { label: '待入庫', color: '#d97706', bg: '#fef3c7' },
  checked_in: { label: '已入庫', color: '#16a34a', bg: '#dcfce7' },
}

function genBatchNo() {
  const d = new Date()
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), day = String(d.getDate()).padStart(2,'0')
  const r = String(Math.floor(Math.random()*900)+100)
  return `${y}${m}${day}-${r}`
}

export default function PackageLabels({ userProfile, products, showToast, addLog }) {
  const isMobile = window.innerWidth <= 768
  const [tab, setTab] = useState('generate') // 'generate' | 'list'
  const [labels, setLabels] = useState([])
  const [loading, setLoading] = useState(false)

  // 產生標籤的表單
  const [genForm, setGenForm] = useState({ productId: '', qtyPerBox: '', boxCount: '', note: '' })
  const [generating, setGenerating] = useState(false)
  const [preview, setPreview] = useState([]) // 預覽要產生的標籤
  const [deleting, setDeleting] = useState(null) // label id 正在刪除中

  // 列表篩選
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterProduct, setFilterProduct] = useState('')

  const productOptions = products.map(p => ({ value: p.id, label: p.name, sub: `庫存: ${p.stock} ${p.unit}` }))

  const loadLabels = useCallback(async () => {
    if (!userProfile?.company_id) return
    setLoading(true)
    let q = supabase.from('package_labels')
      .select('*, products(name, unit, sku)')
      .eq('company_id', userProfile.company_id)
      .order('created_at', { ascending: false })
      .limit(300)
    if (filterStatus !== 'all') q = q.eq('status', filterStatus)
    if (filterProduct) q = q.eq('product_id', filterProduct)
    const { data } = await q
    if (data) setLabels(data)
    setLoading(false)
  }, [userProfile, filterStatus, filterProduct])

  useEffect(() => { if (tab === 'list') loadLabels() }, [tab, loadLabels])

  // 預覽即將產生的標籤
  useEffect(() => {
    const qty = parseInt(genForm.qtyPerBox)
    const count = parseInt(genForm.boxCount)
    const product = products.find(p => p.id === genForm.productId)
    if (!product || !qty || !count || qty <= 0 || count <= 0 || count > 100) {
      setPreview([])
      return
    }
    const batchNo = genBatchNo()
    setPreview(Array.from({ length: Math.min(count, 3) }, (_, i) => ({
      batchNo, seq: i+1, count,
      productName: product.name, qty, unit: product.unit
    })))
  }, [genForm, products])

  const handleGenerate = async () => {
    const qty = parseInt(genForm.qtyPerBox)
    const count = parseInt(genForm.boxCount)
    if (!genForm.productId) { showToast('請選擇商品', 'error'); return }
    if (!qty || qty <= 0) { showToast('請填入每箱數量', 'error'); return }
    if (!count || count <= 0 || count > 100) { showToast('箱數請填 1～100', 'error'); return }

    setGenerating(true)
    const batchNo = genBatchNo()
    const rows = Array.from({ length: count }, () => ({
      company_id: userProfile.company_id,
      product_id: genForm.productId,
      batch_no: batchNo,
      qty,
      note: genForm.note || null,
      status: 'pending',
    }))
    const { data, error } = await supabase.from('package_labels').insert(rows).select()
    if (error) { showToast('產生失敗：' + error.message, 'error'); setGenerating(false); return }
    await addLog('產生包裝標籤', `批次 ${batchNo}，共 ${count} 張，商品：${products.find(p=>p.id===genForm.productId)?.name}`)
    showToast(`✓ 已產生 ${count} 張標籤（批次 ${batchNo}）`)
    setGenerating(false)
    // 直接列印
    handlePrint(data)
    setGenForm({ productId: '', qtyPerBox: '', boxCount: '', note: '' })
    setPreview([])
  }

  const COMPANY_NAME = '盛原企業股份有限公司'

  const buildLabelHtml = (l, product, qrDataUrl, seqLabel) => `
    <div class="label-wrap">
      <table class="lbl" cellspacing="0" cellpadding="0">
        <tr>
          <td colspan="4" class="title">庫存表</td>
        </tr>
        <tr>
          <td class="key-col" rowspan="3">
            <p>客戶:</p>
            <p>尺寸:</p>
            <p>數量:</p>
          </td>
          <td class="cust-val">${l.note || ''}</td>
          <td class="pname-key">品名:</td>
          <td class="pname-col" rowspan="3">
            <div class="pname-text">${product?.name || '—'}</div>
            <img src="${qrDataUrl}" width="120" height="120" class="qr"/>
            <div class="scan-hint">📷 掃描入庫</div>
          </td>
        </tr>
        <tr>
          <td class="sku-val" colspan="2">${product?.sku || ''}</td>
        </tr>
        <tr>
          <td class="qty-val" colspan="2">${l.qty}<span class="unit"> ${product?.unit || ''}</span></td>
        </tr>
      </table>
      <div class="lbl-footer">
        ${COMPANY_NAME}&nbsp;&nbsp;·&nbsp;&nbsp;批次 ${l.batch_no}&nbsp;&nbsp;·&nbsp;&nbsp;${seqLabel}
      </div>
    </div>
  `

  const LABEL_STYLES = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: '標楷體', 'DFKai-SB', serif; background: #fff; }
    .page { padding: 8mm; }
    .label-wrap { margin-bottom: 6mm; page-break-inside: avoid; }
    .lbl { width: 100%; border-collapse: collapse; border: 2.5px solid #000; }
    .lbl td { border: 1.5px solid #000; vertical-align: middle; padding: 4px 8px; }
    .title {
      text-align: center; font-size: 32pt; font-weight: bold;
      letter-spacing: 0.3em; padding: 4px 0;
    }
    .key-col {
      text-align: center; font-size: 14pt; font-weight: bold;
      width: 11%; line-height: 1;
    }
    .key-col p { margin: 10px 0; white-space: nowrap; }
    .cust-val {
      text-align: center; font-size: 14pt; font-weight: bold;
      width: 22%; min-height: 36pt;
    }
    .pname-key {
      text-align: center; font-size: 14pt; font-weight: bold;
      width: 11%;
    }
    .pname-col {
      text-align: center; width: 56%; padding: 8px 10px;
    }
    .pname-text { font-size: 18pt; font-weight: bold; margin-bottom: 6px; line-height: 1.3; }
    .qr { display: block; margin: 4px auto; }
    .scan-hint { font-size: 9pt; color: #555; margin-top: 3px; }
    .sku-val {
      text-align: center; font-size: 16pt; font-weight: bold;
      font-family: Arial, sans-serif; letter-spacing: 0.05em;
    }
    .qty-val {
      text-align: center; font-size: 48pt; font-weight: bold; line-height: 1.1;
    }
    .unit { font-size: 18pt; }
    .lbl-footer {
      text-align: center; font-size: 13pt; margin-top: 3px; padding: 2px 0;
      letter-spacing: 0.05em;
    }
    @media print {
      body { margin: 0; }
      .page { padding: 5mm; }
    }
  `

  const handlePrint = async (labelsToPrint) => {
    const product = products.find(p => p.id === labelsToPrint[0]?.product_id)
    const qrPairs = await Promise.all(
      labelsToPrint.map(l =>
        QRCode.toDataURL(`${window.location.origin}?scan=unit&lid=${l.id}`, { width: 240, margin: 1 }).then(url => [l.id, url])
      )
    )
    const qrMap = Object.fromEntries(qrPairs)
    const labelsHtml = labelsToPrint.map((l, i) =>
      buildLabelHtml(l, product, qrMap[l.id], `第 ${i+1} / ${labelsToPrint.length} 張`)
    ).join('')

    const win = window.open('', '_blank')
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>包裝標籤列印</title>
      <style>${LABEL_STYLES}</style></head>
      <body><div class="page">${labelsHtml}</div>
      <script>window.onload = () => { window.print(); window.onafterprint = () => window.close() }<\/script>
      </body></html>`)
    win.document.close()
  }

  const handleDelete = async (label) => {
    const confirmed = window.confirm(
      `確定要刪除這張標籤嗎？\n商品：${label.products?.name || '—'}\n批次：${label.batch_no}\n數量：${label.qty} ${label.products?.unit || ''}\n\n此操作無法復原。`
    )
    if (!confirmed) return
    setDeleting(label.id)
    const { error } = await supabase
      .from('package_labels')
      .delete()
      .eq('id', label.id)
      .eq('company_id', userProfile.company_id)
    if (error) {
      showToast('刪除失敗：' + error.message, 'error')
    } else {
      showToast('✓ 已刪除標籤')
      setLabels(prev => prev.filter(l => l.id !== label.id))
    }
    setDeleting(null)
  }

  const handleDeleteBatch = async (batchNo) => {
    const batchLabels = labels.filter(l => l.batch_no === batchNo)
    const confirmed = window.confirm(
      `確定要刪除整批「${batchNo}」共 ${batchLabels.length} 張標籤嗎？\n\n此操作無法復原。`
    )
    if (!confirmed) return
    const ids = batchLabels.map(l => l.id)
    const { error } = await supabase
      .from('package_labels')
      .delete()
      .in('id', ids)
      .eq('company_id', userProfile.company_id)
    if (error) {
      showToast('刪除失敗：' + error.message, 'error')
    } else {
      showToast(`✓ 已刪除批次 ${batchNo}（共 ${ids.length} 張）`)
      setLabels(prev => prev.filter(l => l.batch_no !== batchNo))
    }
  }

  const handleRePrint = async (label) => {
    const url = await QRCode.toDataURL(`${window.location.origin}?scan=unit&lid=${label.id}`, { width: 240, margin: 1 })
    const product = label.products
    const statusNote = label.status === 'checked_in' ? '⚠️ 此標籤已入庫，補印僅供參考' : ''
    const lHtml = buildLabelHtml(label, product, url, `補印${statusNote ? ' · ' + statusNote : ''}`)
    const win = window.open('', '_blank')
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>補印標籤</title>
      <style>${LABEL_STYLES}</style></head>
      <body><div class="page">${lHtml}</div>
      <script>window.onload = () => { window.print(); window.onafterprint = () => window.close() }<\/script>
      </body></html>`)
    win.document.close()
  }

  const tabStyle = (key) => ({
    flex: 1, padding: '10px 0', background: 'none', border: 'none',
    borderBottom: tab === key ? `2.5px solid ${C.accent}` : '2.5px solid transparent',
    color: tab === key ? C.accent : C.muted,
    fontSize: 14, fontWeight: tab === key ? 700 : 500, cursor: 'pointer',
  })

  const selectedProduct = products.find(p => p.id === genForm.productId)

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1.5px solid ${C.border}`, marginBottom: 20 }}>
        <button style={tabStyle('generate')} onClick={() => setTab('generate')}>🏷️ 產生標籤</button>
        <button style={tabStyle('list')} onClick={() => setTab('list')}>📋 標籤管理</button>
      </div>

      {/* Tab: 產生標籤 */}
      {tab === 'generate' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px', fontSize: 12, color: '#1e40af', lineHeight: 1.6 }}>
            💡 每次生產完成後，在這裡產生標籤並列印。標籤貼在每箱產品上，入庫時掃描即自動記錄，同一張標籤只能入庫一次。
          </div>

          <div>
            <label style={st.label}>商品 *</label>
            <SearchSelect options={productOptions} value={genForm.productId} onChange={v => setGenForm(f => ({ ...f, productId: v }))} placeholder="選擇商品..." />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <div>
              <label style={st.label}>每箱數量 *</label>
              <input style={st.input} type="number" inputMode="numeric" min="1"
                value={genForm.qtyPerBox} onChange={e => setGenForm(f => ({ ...f, qtyPerBox: e.target.value }))}
                placeholder={`例：24（${selectedProduct?.unit || '個'}）`} />
            </div>
            <div>
              <label style={st.label}>產生幾張 * <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>（最多100）</span></label>
              <input style={st.input} type="number" inputMode="numeric" min="1" max="100"
                value={genForm.boxCount} onChange={e => setGenForm(f => ({ ...f, boxCount: e.target.value }))}
                placeholder="例：10" />
            </div>
          </div>

          <div>
            <label style={st.label}>客戶（選填）<span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>— 會印在標籤上</span></label>
            <input style={st.input} value={genForm.note} onChange={e => setGenForm(f => ({ ...f, note: e.target.value }))} placeholder="例：統一、好市多…" />
          </div>

          {/* 預覽 */}
          {preview.length > 0 && (
            <div style={{ background: C.panel, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, fontWeight: 700 }}>預覽（實際列印 {parseInt(genForm.boxCount)} 張）</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {preview.map((p, i) => (
                  <div key={i} style={{ border: `2px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', textAlign: 'center', minWidth: 120 }}>
                    <div style={{ width: 60, height: 60, background: '#f3f4f6', borderRadius: 4, margin: '0 auto 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>QR</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{p.productName}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{p.qty} {p.unit} / 箱</div>
                    <div style={{ fontSize: 9, color: C.muted, fontFamily: 'monospace' }}>{p.batchNo}</div>
                    <div style={{ fontSize: 9, color: C.muted }}>第 {p.seq}/{p.count} 張</div>
                  </div>
                ))}
                {parseInt(genForm.boxCount) > 3 && (
                  <div style={{ display: 'flex', alignItems: 'center', color: C.muted, fontSize: 12, padding: '0 8px' }}>
                    ...還有 {parseInt(genForm.boxCount) - 3} 張
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={generating || !genForm.productId || !genForm.qtyPerBox || !genForm.boxCount}
            style={{ ...st.btn(generating || !genForm.productId || !genForm.qtyPerBox || !genForm.boxCount ? 'default' : 'success'), padding: '14px', fontSize: 15, fontWeight: 800 }}>
            {generating ? '產生中...' : `🖨️ 產生並列印 ${genForm.boxCount ? genForm.boxCount + ' 張' : ''}標籤`}
          </button>
        </div>
      )}

      {/* Tab: 標籤管理 */}
      {tab === 'list' && (
        <div>
          {/* 篩選 */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <select style={{ ...st.input, width: 'auto', padding: '8px 12px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">全部狀態</option>
              <option value="pending">待入庫</option>
              <option value="checked_in">已入庫</option>
            </select>
            <select style={{ ...st.input, flex: 1, padding: '8px 12px' }} value={filterProduct} onChange={e => setFilterProduct(e.target.value)}>
              <option value="">全部商品</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button onClick={loadLabels} style={{ ...st.btn('default'), padding: '8px 14px' }}>🔄 重新整理</button>
          </div>

          {/* 統計 */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            {['all','pending','checked_in'].map(s => {
              const count = s === 'all' ? labels.length : labels.filter(l => l.status === s).length
              const info = s === 'all' ? { label: '全部', color: C.accent, bg: C.accentDim } : STATUS_LABEL[s]
              return (
                <div key={s} onClick={() => setFilterStatus(s === filterStatus ? 'all' : s)}
                  style={{ flex: 1, background: info.bg, border: `1.5px solid ${info.color}30`, borderRadius: 10, padding: '10px 12px', cursor: 'pointer', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: info.color }}>{count}</div>
                  <div style={{ fontSize: 11, color: info.color }}>{info.label}</div>
                </div>
              )
            })}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: C.muted }}>載入中...</div>
          ) : labels.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: C.muted }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🏷️</div>
              <div>還沒有標籤，到「產生標籤」建立第一批</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* 依批次分組顯示 */}
              {(() => {
                const batchGroups = []
                const seen = {}
                labels.forEach(l => {
                  if (!seen[l.batch_no]) { seen[l.batch_no] = []; batchGroups.push(l.batch_no) }
                  seen[l.batch_no].push(l)
                })
                return batchGroups.map(batchNo => {
                  const batchItems = seen[batchNo]
                  return (
                    <div key={batchNo}>
                      {/* 批次標頭 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.muted, fontWeight: 700 }}>
                          批次 {batchNo}
                        </span>
                        <span style={{ fontSize: 11, color: C.muted }}>（{batchItems.length} 張）</span>
                        <button
                          onClick={() => handleDeleteBatch(batchNo)}
                          style={{ marginLeft: 'auto', background: 'none', border: '1.5px solid #fca5a5', borderRadius: 6, padding: '3px 8px', color: '#dc2626', fontSize: 11, cursor: 'pointer' }}>
                          🗑️ 刪除整批
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {batchItems.map(l => {
                          const statusInfo = STATUS_LABEL[l.status] || STATUS_LABEL.pending
                          const product = l.products
                          return (
                            <div key={l.id} style={{ background: C.panel, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                              {/* 勾選狀態 */}
                              <div style={{
                                width: 28, height: 28, borderRadius: 8,
                                background: l.status === 'checked_in' ? '#dcfce7' : '#f3f4f6',
                                border: `2px solid ${l.status === 'checked_in' ? '#16a34a' : C.border}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0, fontSize: 14,
                              }}>
                                {l.status === 'checked_in' ? '✓' : ''}
                              </div>

                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {product?.name || '—'}
                                  </span>
                                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: statusInfo.bg, color: statusInfo.color, fontWeight: 700, flexShrink: 0 }}>
                                    {statusInfo.label}
                                  </span>
                                </div>
                                <div style={{ fontSize: 11, color: C.muted }}>
                                  {l.qty} {product?.unit}
                                  {l.status === 'checked_in' && l.checked_in_by && ` · ${l.checked_in_by} 入庫`}
                                  {l.status === 'checked_in' && l.checked_in_at && ` · ${new Date(l.checked_in_at).toLocaleDateString('zh-TW')} ${new Date(l.checked_in_at).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}`}
                                </div>
                              </div>

                              <button onClick={() => handleRePrint(l)}
                                style={{ background: 'none', border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '6px 8px', color: C.muted, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                                🖨️ 補印
                              </button>
                              <button
                                onClick={() => handleDelete(l)}
                                disabled={deleting === l.id}
                                style={{ background: 'none', border: '1.5px solid #fca5a5', borderRadius: 8, padding: '6px 8px', color: deleting === l.id ? C.muted : '#dc2626', fontSize: 12, cursor: deleting === l.id ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
                                {deleting === l.id ? '...' : '🗑️'}
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
